import produce from 'immer';
import { getFeatureFlag } from 'services/featureFlag';
import { create } from 'zustand';

export type FeatureFlag = {
  name: string;
  isEnabled: boolean;
};

export type EmailFeatureFlag = {
  email: string;
  isEnabled: boolean;
  isLayoutEnabled: boolean;
};

export type FeatureFlagState = {
  flags: FeatureFlag[];
  isLoading: boolean;
  isError: boolean;
  isSuccessful: boolean;
};

export type FeatureFlagActions = {
  fetchFeatureFlags: () => Promise<void>;
  refreshFeatureFlags: () => Promise<void>;
  refreshSingleFeatureFlag: (flagName: FeatureFlags) => Promise<void>;
  checkEmailBasedFeatures: (email: string) => Promise<void>;
  setState: (newState: Partial<FeatureFlagState>) => void;
  isFeatureFlagEnabled: (flagName: FeatureFlags) => boolean;
  isLkpGfpEnabledForEmail: (email: string) => boolean;
};

export type FeatureFlagStore = FeatureFlagState & FeatureFlagActions;

export enum FeatureFlags {
  DeskManagerServiceRequestsOnECommerce = 'DeskManagerServiceRequestsOnECommerce',
  LkpGfpIsEnable = 'LkpGfp:IsEnable',
}

export enum EmailFeatureFlags {
  LkpGfp = 'LkpGfp',
}

const initialFeatureFlagState: FeatureFlagState = {
  flags: [
    {
      name: FeatureFlags.DeskManagerServiceRequestsOnECommerce,
      isEnabled: false,
    },
    { name: FeatureFlags.LkpGfpIsEnable, isEnabled: false },
  ],
  isLoading: false,
  isError: false,
  isSuccessful: false,
};

const predefinedFlags = Object.values(FeatureFlags);

const EMAIL_LKP_GFP_FLAG_PREFIX = 'LkpGfp:' as const;

const createEmailFlag = (email: string): string =>
  `${EMAIL_LKP_GFP_FLAG_PREFIX}${email}`;

const createLoadingState = (isLoading: boolean) => ({
  isLoading,
  isError: false,
  isSuccessful: !isLoading,
});

const createSuccessState = () => ({
  isLoading: false,
  isError: false,
  isSuccessful: true,
});

const createErrorState = () => ({
  isLoading: false,
  isError: true,
  isSuccessful: false,
});

export const useFeatureFlagStore = create<FeatureFlagStore>()((set, get) => ({
  ...initialFeatureFlagState,

  setState: (newState: Partial<FeatureFlagState>) => {
    set(
      produce((state: FeatureFlagStore) => {
        Object.assign(state, newState);
      }),
    );
  },

  fetchFeatureFlags: async () => {
    const { setState } = get();

    setState({ isLoading: true });

    try {
      const flagResults = await Promise.allSettled(
        predefinedFlags.map((flag) => getFeatureFlag({ flag })),
      );

      const flags = flagResults.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        }
        console.warn(
          `Failed to fetch feature flag ${predefinedFlags[index]}:`,
          result.reason,
        );
        return {
          name: predefinedFlags[index],
          isEnabled: false,
        };
      });

      setState({
        flags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      setState({
        flags: initialFeatureFlagState.flags,
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  refreshFeatureFlags: async () => {
    const { fetchFeatureFlags } = get();
    await fetchFeatureFlags();
  },

  refreshSingleFeatureFlag: async (flagName: FeatureFlags) => {
    const { setState, flags } = get();

    try {
      const updatedFlag = await getFeatureFlag({ flag: flagName });
      const updatedFlags = flags.map((flag) =>
        flag.name === flagName ? updatedFlag : flag,
      );

      setState({
        flags: updatedFlags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      setState({
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  checkEmailBasedFeatures: async (email: string) => {
    if (!email) return;

    const { setState } = get();
    setState(createLoadingState(true));

    try {
      const emailFlagName = createEmailFlag(email);

      const emailFlagResult = await Promise.allSettled([
        getFeatureFlag({ flag: emailFlagName }),
      ]);

      const emailFlag =
        emailFlagResult[0].status === 'fulfilled'
          ? emailFlagResult[0].value
          : { name: emailFlagName, isEnabled: false };

      const currentFlags = get().flags;
      const updatedFlags = [
        ...currentFlags.filter((flag) => flag.name !== emailFlagName),
        emailFlag,
      ];

      setState({
        flags: updatedFlags,
        ...createSuccessState(),
      });
    } catch (error) {
      setState(createErrorState());
    }
  },

  isLkpGfpEnabledForEmail: (email: string): boolean => {
    if (!email) return false;

    try {
      const emailFlagName = createEmailFlag(email);
      const emailFlag = get().flags.find((flag) => flag.name === emailFlagName);
      return emailFlag?.isEnabled ?? false;
    } catch {
      return false;
    }
  },

  isFeatureFlagEnabled: (flagName): boolean => {
    const { flags } = get();
    const flag = flags?.find((it) => it.name === flagName);
    return flag?.isEnabled ?? false;
  },
}));
